/**
 * Results that finish for a workspace that is not open.
 *
 * A generation keeps running when the user switches to another workspace. When it finishes,
 * its node is not on the canvas, so its result (the image or video, the status, the cost) is
 * held here, under the workspace and node it belongs to, until that workspace is open again.
 * Results are kept in this browser as well, so one that finishes just before a reload is not
 * lost either.
 */
import type { NodeData } from '../types';

export interface HeldResult {
  workspaceKey: string;
  nodeId: string;
  patch: Partial<NodeData>;
  heldAt: number;
}

/** A check against the node's data when the result is applied (only known in this session). */
export type ResultGuard = (current: NodeData) => boolean;

const DB_NAME = 'NoodleHeldResults';
const STORE = 'results';
/** A result whose node never comes back (it was deleted) is let go after this long. */
const KEEP_MS = 30 * 24 * 60 * 60 * 1000;

const keyOf = (workspaceKey: string, nodeId: string) => `${workspaceKey}|${nodeId}`;

const held = new Map<string, HeldResult>();
const guards = new Map<string, ResultGuard>();
let loaded: Promise<void> | null = null;

const openDb = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') { reject(new Error('No IndexedDB')); return; }
  const request = indexedDB.open(DB_NAME, 1);
  request.onupgradeneeded = () => { if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE); };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const withStore = async (mode: IDBTransactionMode, work: (store: IDBObjectStore) => void): Promise<void> => {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      work(transaction.objectStore(STORE));
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    db.close();
  }
};

/** Read what this browser holds, once; old results whose node never returned are dropped. */
const load = (): Promise<void> => {
  if (!loaded) {
    loaded = (async () => {
      const values: HeldResult[] = [];
      try {
        await withStore('readonly', (store) => {
          const request = store.getAll();
          request.onsuccess = () => values.push(...(request.result as HeldResult[]));
        });
      } catch { /* not available: results are held for this session only */ }
      const now = Date.now();
      for (const value of values) {
        if (now - value.heldAt > KEEP_MS) { void forget(value.workspaceKey, value.nodeId); continue; }
        const key = keyOf(value.workspaceKey, value.nodeId);
        if (!held.has(key)) held.set(key, value);
      }
    })();
  }
  return loaded;
};

/**
 * Hold a node's result for its workspace. A later result for the same node adds to the
 * earlier one (the latest values win).
 */
export const holdNodeResult = async (workspaceKey: string, nodeId: string, patch: Partial<NodeData>, guard?: ResultGuard): Promise<void> => {
  await load();
  const key = keyOf(workspaceKey, nodeId);
  const value: HeldResult = { workspaceKey, nodeId, patch: { ...held.get(key)?.patch, ...patch }, heldAt: Date.now() };
  held.set(key, value);
  if (guard) guards.set(key, guard);
  try {
    await withStore('readwrite', store => { store.put(value, key); });
  } catch { /* kept in memory for this session */ }
};

/** The results held for a workspace, with the session's checks where known. */
export const heldResultsFor = async (workspaceKey: string): Promise<(HeldResult & { guard?: ResultGuard })[]> => {
  await load();
  return [...held.values()]
    .filter(value => value.workspaceKey === workspaceKey)
    .map(value => ({ ...value, guard: guards.get(keyOf(value.workspaceKey, value.nodeId)) }));
};

/** Whether anything is held for a workspace, without waiting (false until the store has been read). */
export const hasHeldResults = (workspaceKey: string): boolean => {
  void load();
  for (const value of held.values()) if (value.workspaceKey === workspaceKey) return true;
  return false;
};

/** Let a result go once it is on its node (or can no longer be applied). */
export const forget = async (workspaceKey: string, nodeId: string): Promise<void> => {
  const key = keyOf(workspaceKey, nodeId);
  held.delete(key);
  guards.delete(key);
  try {
    await withStore('readwrite', store => { store.delete(key); });
  } catch { /* nothing kept to remove */ }
};

/** Read the store now, so results held before a reload are known when the first workspace opens. */
export const loadHeldResults = (): Promise<void> => load();
